import { useEffect, useState } from "react";
import { Search } from "lucide-react";
import { useApi } from "@/lib/api";
import { useSeo, SUPPORT_TYPES, AUDIENCES } from "@/lib/site";
import { PageIntro } from "@/components/site/PageIntro";
import { ServiceCard } from "@/components/site/ServiceCard";

const Select = ({ id, label, value, onChange, options, testId }) => (
  <div>
    <label htmlFor={id} className="field-label">{label}</label>
    <select id={id} className="field" value={value} onChange={(e) => onChange(e.target.value)} data-testid={testId}>
      <option value="">All</option>
      {Object.entries(options).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
    </select>
  </div>
);

export default function Directory() {
  useSeo("Support Directory", "A searchable directory of established UK organisations supporting people affected by someone else's addiction.");
  const [q, setQ] = useState("");
  const [dq, setDq] = useState("");
  const [type, setType] = useState("");
  const [audience, setAudience] = useState("");
  useEffect(() => {
    const t = setTimeout(() => setDq(q), 250);
    return () => clearTimeout(t);
  }, [q]);
  const params = new URLSearchParams({ ...(type && { type }), ...(audience && { audience }), ...(dq && { q: dq }) });
  const { data, loading } = useApi(`/directory?${params}`);
  return (
    <>
      <PageIntro eyebrow="Support Directory" title="Trusted UK support, in one place." testId="directory-intro">
        <p>Established organisations that support families, partners and friends, as well as people in crisis. Each entry shows when its details were last checked. Always check an organisation's own website for the latest information.</p>
        <p className="text-sm">Listing an organisation does not mean it endorses, or is affiliated with, Addiction &amp; Us.</p>
      </PageIntro>
      <section className="container-site" aria-label="Directory">
        <div className="card-soft p-5 md:p-6 grid gap-4 md:grid-cols-[2fr_1fr_1fr] items-end mb-8" data-testid="directory-filters">
          <div>
            <label htmlFor="dir-q" className="field-label">Search by name or keyword</label>
            <div className="relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-ink-muted" aria-hidden="true" />
              <input id="dir-q" type="search" className="field pl-12" value={q} onChange={(e) => setQ(e.target.value)} placeholder="e.g. alcohol, groups, family" data-testid="directory-search-input" />
            </div>
          </div>
          <Select id="dir-type" label="Type of support" value={type} onChange={setType} options={SUPPORT_TYPES} testId="directory-type-filter" />
          <Select id="dir-aud" label="Who it's for" value={audience} onChange={setAudience} options={AUDIENCES} testId="directory-audience-filter" />
        </div>
        <p className="text-sm text-ink-muted mb-4" aria-live="polite" data-testid="directory-count">{loading ? "Loading…" : `${data?.length || 0} organisations`}</p>
        {!loading && data?.length === 0 && <div className="card-soft p-8 text-ink-soft" data-testid="directory-no-results">No organisations match those filters. Try widening your search.</div>}
        <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3" data-testid="directory-grid">{(data || []).map((s) => <ServiceCard key={s.slug} entry={s} />)}</div>
      </section>
    </>
  );
}
